import type { Dispatch, SetStateAction } from 'react';
import {
  ELEMENT_KINDS,
  PILE_LEVELS,
  type GeoElementKind,
  type GeoPileLevel,
  type GeoSheetSource,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  Check,
  FieldGrid,
  FieldLabel,
  Hint,
  LinkButton,
  Muted,
  Notice,
  NumInput,
  Row,
  Stack,
} from '@widgets/GeoSchemes';
import { Count, PickList, Sub } from './styles';

interface Props {
  sheet: GeoSheetSource;
  sheetBound: boolean;
  goTo: (step: number) => void;
  what: 'elements' | 'outlines';
  setWhat: (what: 'elements' | 'outlines') => void;
  surveyCount: number;
  hTop: number | null;
  setHTop: (h: number | null) => void;
  pileLevel: GeoPileLevel;
  setPileLevel: (level: GeoPileLevel) => void;
  layers: string[];
  setLayers: Dispatch<SetStateAction<string[]>>;
  blocks: string[];
  setBlocks: Dispatch<SetStateAction<string[]>>;
  circles: boolean;
  setCircles: (c: boolean) => void;
  points: boolean;
  setPoints: (p: boolean) => void;
  kind: GeoElementKind;
  setKind: (k: GeoElementKind) => void;
  sizeMm: number | null;
  setSizeMm: (s: number | null) => void;
  busy: boolean;
  error: string | null;
  onExtract: () => void;
}

export function SheetExtractSection({
  sheet,
  sheetBound,
  goTo,
  what,
  setWhat,
  surveyCount,
  hTop,
  setHTop,
  pileLevel,
  setPileLevel,
  layers,
  setLayers,
  blocks,
  setBlocks,
  circles,
  setCircles,
  points,
  setPoints,
  kind,
  setKind,
  sizeMm,
  setSizeMm,
  busy,
  error,
  onExtract,
}: Props) {
  return (
    <Stack $gap={3}>
      <Sub>Извлечь из проектного листа</Sub>
      {!sheetBound && (
        <Notice $tone="warning" role="status">
          Сначала привяжите лист к координатам площадки — без привязки элементы листа не
          перевести в МСК.{' '}
          <LinkButton type="button" onClick={() => goTo(2)}>
            Привязка листа — шаг 2 →
          </LinkButton>
        </Notice>
      )}
      <FieldGrid $min={260}>
        <FieldLabel>
          Что извлечь
          <Select
            value={what}
            onChange={(e) => setWhat(e.target.value === 'outlines' ? 'outlines' : 'elements')}
          >
            <option value="outlines">
              {surveyCount > 0
                ? 'Контуры конструкций у точек съёмки (бетон)'
                : 'Конструкции по маркам листа (бетон, для выноса)'}
            </option>
            <option value="elements">
              {sheet.kind === 'pdf'
                ? 'Сваи (кружки и квадраты с номерами)'
                : 'Элементы по слоям и блокам DXF'}
            </option>
          </Select>
        </FieldLabel>
      </FieldGrid>
      {what === 'outlines' ? (
        <>
          <Hint>
            {surveyCount > 0
              ? 'Балки, ростверки, фундаменты, стены — замкнутые контуры листа рядом с точками съёмки; круглые фундаменты, колонны, колодцы — окружностью (центр и диаметр): стена сличается по радиусу. Марка берётся с листа (по выноске или рядом); одинаковые марки различаются: «Фм-1», «Фм-1·2».'
              : 'Съёмки ещё нет — программа найдёт контур у каждой марки конструкции листа («Фм1», «Рм-4», «ФПм-1»): по выноске или вокруг надписи; таблицы и закладные пропускаются. Углы контуров — в каталоге для выноса в натуру. Когда загрузите съёмку, найдите контуры заново у её точек — для исполнительной схемы так точнее.'}
          </Hint>
          <FieldGrid $min={200}>
            <FieldLabel>
              Отметка верха H, м
              <NumInput value={hTop} onValue={setHTop} placeholder="например, 99,867" />
              <Muted>
                Можно оставить пустой и вписать потом в таблице — у каждого контура.
              </Muted>
            </FieldLabel>
          </FieldGrid>
        </>
      ) : sheet.kind === 'pdf' ? (
        <>
          <Hint>
            Сваи листа: квадраты (забивные) и кружки с крестом (буронабивные) с номерами.
            Отметки, марку и сечение программа берёт из таблицы листа («Таблица отметок свай»,
            «Экспликация свай») по номеру сваи.
          </Hint>
          <FieldGrid $min={260}>
            <FieldLabel>
              Проектная отметка головы
              <Select
                value={pileLevel}
                onChange={(e) =>
                  setPileLevel(e.target.value === 'head_driven' ? 'head_driven' : 'head_cut')
                }
              >
                {PILE_LEVELS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </FieldGrid>
        </>
      ) : (
        <>
          <FieldGrid $min={200}>
            {sheet.layers.length > 0 && (
              <FieldLabel as="div">
                Слои
                <PickList role="group" aria-label="Слои DXF">
                  {sheet.layers.map((l) => (
                    <Check
                      key={l.name}
                      checked={layers.includes(l.name)}
                      onChange={(on) =>
                        setLayers((prev) =>
                          on ? [...prev, l.name] : prev.filter((x) => x !== l.name),
                        )
                      }
                    >
                      {l.name} <Count>{l.count}</Count>
                    </Check>
                  ))}
                </PickList>
              </FieldLabel>
            )}
            {sheet.blocks.length > 0 && (
              <FieldLabel as="div">
                Блоки
                <PickList role="group" aria-label="Блоки DXF">
                  {sheet.blocks.map((bl) => (
                    <Check
                      key={bl.name}
                      checked={blocks.includes(bl.name)}
                      onChange={(on) =>
                        setBlocks((prev) =>
                          on ? [...prev, bl.name] : prev.filter((x) => x !== bl.name),
                        )
                      }
                    >
                      {bl.name} <Count>{bl.count}</Count>
                    </Check>
                  ))}
                </PickList>
              </FieldLabel>
            )}
          </FieldGrid>
          <Row $gap={4}>
            <Check checked={circles} onChange={setCircles}>
              окружности
            </Check>
            <Check checked={points} onChange={setPoints}>
              точки
            </Check>
          </Row>
          <FieldGrid $min={160}>
            <FieldLabel>
              Вид элементов
              <Select
                value={kind}
                onChange={(e) => {
                  const k = ELEMENT_KINDS.find((x) => x.value === e.target.value);
                  if (k) setKind(k.value);
                }}
              >
                {ELEMENT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </FieldGrid>
        </>
      )}
      {what === 'elements' && (
        <FieldGrid $min={160}>
          <FieldLabel>
            Размер d, мм
            <NumInput
              value={sizeMm}
              onValue={setSizeMm}
              placeholder={sheet.kind === 'pdf' ? 'из таблицы листа' : 'например, 400'}
            />
            <Muted>
              {sheet.kind === 'pdf'
                ? 'Пусто — сечение из таблицы свай листа, а без неё — по размеру знака.'
                : 'Пусто — по размеру знака на листе.'}
            </Muted>
          </FieldLabel>
        </FieldGrid>
      )}
      <div>
        <Button
          type="button"
          $variant="secondary"
          onClick={onExtract}
          disabled={
            busy ||
            !sheetBound ||
            (what === 'elements' &&
              sheet.kind === 'dxf' &&
              layers.length === 0 &&
              blocks.length === 0)
          }
        >
          {busy
            ? 'Извлекаем…'
            : what === 'outlines'
              ? surveyCount > 0
                ? 'Найти контуры у точек съёмки'
                : 'Найти конструкции по маркам'
              : sheet.kind === 'pdf'
                ? 'Извлечь сваи из листа'
                : 'Извлечь элементы из DXF'}
        </Button>
      </div>
      {error && (
        <Notice $tone="danger" role="alert">
          {error}
        </Notice>
      )}
    </Stack>
  );
}
